
import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLocalStorage } from "../componentUtils/LocalStorage";
import GetCustomerContracts from "../utils/GetCustomerContracts";

interface CustomerContract {
  contract_id: number;
  contract_name: string | null;
  contract_start_dt: string | null;
  contract_end_dt: string | null;
  contract_info: string | null;
}

interface CustomerContractsProps {
  customerNum: string;
}

const formatDate = (value: string | null) => {
  if (!value) return "";
  return value.substring(0, 10);
};

export default function CustomerContractsDisp({
  customerNum,
}: CustomerContractsProps) {
  const [salesOrg] = useLocalStorage("SETTINGS.salesOrg", "");

  const [contracts, setContracts] = useState<CustomerContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!customerNum || !salesOrg) {
      setContracts([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(false);

    GetCustomerContracts(salesOrg.toString(), customerNum)
      .then((data) => {
        if (!cancelled) {
          setContracts(data || []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err);
          setError(true);
          setContracts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [salesOrg, customerNum]);

  return (
    <Accordion sx={{ mt: 2, mb: 1 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="customer-contracts-content"
        id="customer-contracts-header"
      >
        <Typography>
          Contracts ({contracts.length})
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        {loading ? (
          <CircularProgress size={24} />
        ) : error ? (
          <Typography color="error">
            Failed to load customer contracts.
          </Typography>
        ) : contracts.length === 0 ? (
          <Typography color="textSecondary">
            No contracts assigned to this customer.
          </Typography>
        ) : (
          contracts.map((contract) => (
            <Grid
              container
              spacing={2}
              key={contract.contract_id}
              sx={{
                mb: 2,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Contract Name"
                  value={contract.contract_name || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Contract Start Date"
                  value={formatDate(contract.contract_start_dt)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Contract End Date"
                  value={formatDate(contract.contract_end_dt)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>


              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Contract Info"
                  value={contract.contract_info || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          ))
        )}
      </AccordionDetails>
    </Accordion>
  );
}
